import { WORK_TYPE } from '../constants';
import type { ICalendarDay, ICalendarDayMake } from '../interfaces/ICalendarDay';
import type { WorkType } from '../types/WorkType';
import type { TimezoneMode } from '../interfaces/IOption';
import {
  createDateWithTimezone,
  getDateComponentsWithTimezone,
  getMidnightTimestampWithTimezone,
} from '../utils/dateUtils';

export class CalendarDay implements ICalendarDay {
  public isCurrentMonth: boolean = false;
  public day: number = 0;
  public isActive: boolean = false;
  public isToday: boolean = false;

  public constructor(
    day: number,
    isCurrentMonth: boolean = false,
    isToday: boolean = false,
    isActive: boolean = false,
  ) {
    this.day = day;
    this.isCurrentMonth = isCurrentMonth;
    this.isToday = isToday;
    this.isActive = isActive;
  }

  public static make(data: ICalendarDayMake): ICalendarDay[] {
    const {
      startDate,
      endDate,
      todayDate,
      selectedDate,
      isCurrentMonth,
      workType,
      timezone = 'local',
    } = data;

    // Используем timezone-aware сравнение дат
    const startComponents = getDateComponentsWithTimezone(startDate, timezone);
    const todayComponents = getDateComponentsWithTimezone(todayDate, timezone);

    const endComponents = getDateComponentsWithTimezone(endDate, timezone);
    const needToday =
      startComponents.year === todayComponents.year &&
      startComponents.month === todayComponents.month;

    const needActive = this.needSelected(selectedDate ?? [], startDate, workType, timezone);
    const todayNumber = todayComponents.day;

    const result = [];
    for (let i = startComponents.day; i <= endComponents.day; i++) {
      // Используем timezone-aware создание даты
      const currentDate = createDateWithTimezone(
        startComponents.year,
        startComponents.month,
        i,
        timezone,
      );
      const isToday = needToday && todayNumber === i;
      const isActive =
        needActive && this.isActive(selectedDate ?? [], currentDate, workType, timezone);
      result.push(new CalendarDay(i, isCurrentMonth, isToday, isActive));
    }

    return result;
  }

  public static needSelected(
    selectedDate: Date[],
    date: Date,
    workType: WorkType,
    timezone: TimezoneMode = 'local',
  ): boolean {
    if (selectedDate.length === 0) {
      return false;
    }

    if (workType === WORK_TYPE.SINGLE) {
      const selected = selectedDate[0];
      // Используем timezone-aware сравнение дат
      const dateComponents = getDateComponentsWithTimezone(date, timezone);
      const selectedComponents = getDateComponentsWithTimezone(selected, timezone);
      return (
        dateComponents.year === selectedComponents.year &&
        dateComponents.month === selectedComponents.month
      );
    }

    if (workType === WORK_TYPE.MULTI) {
      return true;
    }

    return false;
  }

  public static isActive(
    selectedDate: Date[],
    date: Date,
    workType: WorkType,
    timezone: TimezoneMode = 'local',
  ): boolean {
    if (selectedDate.length === 0) {
      return false;
    }

    if (workType === WORK_TYPE.SINGLE) {
      const selected = selectedDate[0];
      // Используем timezone-aware сравнение дат
      const dateComponents = getDateComponentsWithTimezone(date, timezone);
      const selectedComponents = getDateComponentsWithTimezone(selected, timezone);
      return (
        dateComponents.year === selectedComponents.year &&
        dateComponents.month === selectedComponents.month &&
        dateComponents.day === selectedComponents.day
      );
    }

    if (workType === WORK_TYPE.MULTI) {
      if (selectedDate.length === 1) {
        const start = selectedDate[0];
        // Используем timezone-aware сравнение дат
        const dateComponents = getDateComponentsWithTimezone(date, timezone);
        const startComponents = getDateComponentsWithTimezone(start, timezone);
        return (
          dateComponents.year === startComponents.year &&
          dateComponents.month === startComponents.month &&
          dateComponents.day === startComponents.day
        );
      }

      const sorted = [...selectedDate].sort((a, b) => a.getTime() - b.getTime());
      const start = sorted[0];
      const end = sorted[1];

      // Use timezone-aware date comparison
      const currentTs = getMidnightTimestampWithTimezone(date, timezone);
      const startTs = getMidnightTimestampWithTimezone(start, timezone);
      const endTs = getMidnightTimestampWithTimezone(end, timezone);

      return currentTs >= startTs && currentTs <= endTs;
    }

    return false;
  }
}
